import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import "./legal.css";

type Section = {
  id: string;
  title: string;
  body: React.ReactNode;
};

type LegalContent = {
  eyebrow: string;
  titleStart: string;
  titleSerif: string;
  effective: string;
  updated: string;
  intro: React.ReactNode;
  sections: Section[];
};

function slug(s: string) {
  return s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function LegalPage({ content, otherLink }: { content: LegalContent; otherLink: { href: string; label: string } }) {
  const navigate = useNavigate();

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/signin");
  };

  return (
    <div className="legal-root">
      <header className="legal-header">
        <div className="legal-header-inner">
          <Link to="/" className="legal-wordmark" aria-label="Book and Go">
            <span className="w">Book</span>
            <span className="amp">&amp;</span>
            <span className="w">Go</span>
          </Link>
          <button type="button" className="legal-back" onClick={goBack}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M11 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
      </header>

      <main className="legal-main">
        <div className="legal-eyebrow">{content.eyebrow}</div>
        <h1 className="legal-title">
          {content.titleStart} <span className="serif">{content.titleSerif}</span>
        </h1>
        <p className="legal-meta">
          Effective <strong>{content.effective}</strong>
          <span style={{ margin: "0 12px", opacity: 0.4 }}>·</span>
          Last updated <strong>{content.updated}</strong>
        </p>

        <div className="legal-toc" aria-label="Table of contents">
          <div className="legal-toc-label">Contents</div>
          <ol>
            {content.sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </div>

        <div className="legal-section">{content.intro}</div>

        {content.sections.map((s, i) => (
          <section key={s.id} id={s.id} className="legal-section">
            <div className="legal-section-heading">
              <span className="legal-section-num">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2>{s.title}</h2>
            </div>
            {s.body}
          </section>
        ))}
      </main>

      <footer className="legal-footer">
        <span>Book &amp; Go · v3.4.1</span>
        <span>
          <Link to={otherLink.href}>{otherLink.label}</Link>
        </span>
      </footer>
    </div>
  );
}

/* ============================== PRIVACY ============================== */

const PRIVACY: LegalContent = {
  eyebrow: "Operator Portal · Legal",
  titleStart: "Privacy",
  titleSerif: "policy.",
  effective: "1 Jan 2026",
  updated: "23 May 2026",
  intro: (
    <p>
      This Privacy Policy explains how <strong>Book &amp; Go</strong> ("we",
      "us", "our") collects, uses, and protects information when you use the
      Operator Portal and related services (the "Service"). It applies to
      venue operators, staff, and administrators with an account. End-customer
      privacy is governed separately by each venue's own privacy notice.
    </p>
  ),
  sections: [
    {
      id: slug("Information we collect"),
      title: "Information we collect",
      body: (
        <>
          <p>We collect the following categories of information:</p>
          <ul>
            <li>
              <strong>Account data:</strong> name, email, phone, role, venue
              and location associations, profile photo.
            </li>
            <li>
              <strong>Operational data:</strong> bookings, schedules, member
              records, communications, payments and refunds processed through
              the Service.
            </li>
            <li>
              <strong>Device &amp; usage data:</strong> IP address, browser
              type, pages visited, actions taken, timestamps, error logs.
            </li>
            <li>
              <strong>Cookies &amp; local storage:</strong> session tokens,
              preferences, feature flags (see Section 8).
            </li>
          </ul>
        </>
      ),
    },
    {
      id: slug("How we use information"),
      title: "How we use information",
      body: (
        <>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the Service.</li>
            <li>
              Authenticate operators, enforce role-based access, and detect
              fraud or abuse.
            </li>
            <li>
              Process bookings and payments, issue receipts, and handle
              refunds on behalf of venues.
            </li>
            <li>
              Send transactional communications (password resets, security
              alerts, service updates).
            </li>
            <li>
              Generate aggregated, de-identified analytics about usage of the
              Service.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: slug("Legal bases (GDPR)"),
      title: "Legal bases (GDPR)",
      body: (
        <p>
          Where the GDPR applies, we process personal data on the basis of (a)
          performance of the contract with the venue operator, (b) compliance
          with legal obligations, (c) our legitimate interests in operating
          and securing the Service, or (d) consent where required (for
          example, optional analytics cookies).
        </p>
      ),
    },
    {
      id: slug("Sharing"),
      title: "Sharing",
      body: (
        <>
          <p>We do not sell personal data. We share data only with:</p>
          <ul>
            <li>
              <strong>Service providers</strong> processing data on our
              behalf (cloud hosting, payment processors, email delivery, error
              monitoring) under written agreements.
            </li>
            <li>
              <strong>Your venue</strong> — operators see and manage data
              within the venues and locations they are assigned to.
            </li>
            <li>
              <strong>Authorities</strong> when required by law, court order,
              or to protect the rights, safety, and property of Book &amp; Go,
              its operators, or customers.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: slug("International transfers"),
      title: "International transfers",
      body: (
        <p>
          Our infrastructure is hosted in the European Union and
          Asia-Pacific. When data is transferred outside the European
          Economic Area, we rely on Standard Contractual Clauses or other
          recognised safeguards.
        </p>
      ),
    },
    {
      id: slug("Retention"),
      title: "Retention",
      body: (
        <p>
          Operational data is retained for as long as the venue maintains an
          active account, plus the period required for tax, accounting, and
          dispute resolution (typically up to 7 years). Audit logs are kept
          for 24 months. You can request earlier deletion subject to legal
          retention obligations.
        </p>
      ),
    },
    {
      id: slug("Your rights"),
      title: "Your rights",
      body: (
        <>
          <p>
            Depending on your jurisdiction, you may have the right to access,
            correct, delete, port, or restrict the processing of your personal
            data, and to object to certain processing. To exercise these
            rights, contact us at{" "}
            <a href="mailto:privacy@bookandgo.app">privacy@bookandgo.app</a>.
            We respond within 30 days.
          </p>
          <div className="legal-callout">
            <strong>EU/UK residents</strong>
            You have the right to lodge a complaint with your local
            supervisory authority if you believe your data has been handled
            improperly.
          </div>
        </>
      ),
    },
    {
      id: slug("Cookies"),
      title: "Cookies",
      body: (
        <p>
          We use strictly necessary cookies and local storage to keep you
          signed in, remember your preferences, and maintain session
          security. We use first-party analytics to understand product
          usage. We do not use advertising cookies.
        </p>
      ),
    },
    {
      id: slug("Security"),
      title: "Security",
      body: (
        <p>
          We use encryption in transit (TLS 1.2+) and at rest, role-based
          access controls, audit logging, and regular vulnerability scans.
          No system is perfectly secure — promptly notify us of any
          suspected compromise at{" "}
          <a href="mailto:security@bookandgo.app">security@bookandgo.app</a>.
        </p>
      ),
    },
    {
      id: slug("Children"),
      title: "Children",
      body: (
        <p>
          The Operator Portal is intended for users aged 18 or older. We do
          not knowingly collect personal information from children under 16
          through the Operator Portal.
        </p>
      ),
    },
    {
      id: slug("Changes"),
      title: "Changes",
      body: (
        <p>
          We may update this Policy from time to time. Material changes will
          be notified via the Operator Portal or by email at least 30 days
          before they take effect. The "Last updated" date at the top of
          this page reflects the most recent revision.
        </p>
      ),
    },
    {
      id: slug("Contact"),
      title: "Contact",
      body: (
        <p>
          Questions or requests? Reach the Privacy Team at{" "}
          <a href="mailto:privacy@bookandgo.app">privacy@bookandgo.app</a>{" "}
          or the general support team at{" "}
          <a href="mailto:support@bookandgo.app">support@bookandgo.app</a>.
        </p>
      ),
    },
  ],
};

/* ============================== TERMS ============================== */

const TERMS: LegalContent = {
  eyebrow: "Operator Portal · Legal",
  titleStart: "Terms &",
  titleSerif: "conditions.",
  effective: "1 Jan 2026",
  updated: "23 May 2026",
  intro: (
    <p>
      These Terms and Conditions ("Terms") govern access to and use of the{" "}
      <strong>Book &amp; Go</strong> Operator Portal and related services
      (the "Service"). By signing in, you agree to these Terms on behalf of
      yourself and the venue you represent.
    </p>
  ),
  sections: [
    {
      id: slug("The Service"),
      title: "The Service",
      body: (
        <p>
          Book &amp; Go provides a multi-tenant back-office platform for
          sports and leisure venues, including bookings, scheduling,
          memberships, payments, customer management, and reporting. We may
          modify or add features at our discretion. Material reductions of
          paid functionality will be announced in advance.
        </p>
      ),
    },
    {
      id: slug("Accounts"),
      title: "Accounts",
      body: (
        <>
          <p>
            Each operator account is personal and may not be shared. You are
            responsible for keeping credentials confidential and for all
            activity that occurs under your account. Notify us immediately
            of any unauthorised access at{" "}
            <a href="mailto:security@bookandgo.app">security@bookandgo.app</a>.
          </p>
          <p>
            Role-based access is determined by the venue's administrator.
            Roles include Super Admin, Club Admin, Location Owner, Staff
            Manager, Staff, and Player. Access is revoked when your
            employment or engagement with the venue ends.
          </p>
        </>
      ),
    },
    {
      id: slug("Acceptable use"),
      title: "Acceptable use",
      body: (
        <>
          <p>You agree not to:</p>
          <ul>
            <li>
              Use the Service to violate any law or third-party right.
            </li>
            <li>
              Upload malware, attempt to bypass access controls, or probe
              the system for vulnerabilities without prior written consent.
            </li>
            <li>
              Reverse-engineer, decompile, or scrape the Service except as
              expressly permitted by law.
            </li>
            <li>
              Send unsolicited marketing or transactional messages through
              the Service to recipients without a lawful basis.
            </li>
            <li>
              Resell, white-label, or sublicense the Service without a
              written agreement with Book &amp; Go.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: slug("Fees & billing"),
      title: "Fees and billing",
      body: (
        <p>
          Subscription fees, transaction fees, and any usage-based charges
          are set in your venue's commercial agreement with Book &amp; Go.
          Fees are non-refundable except where required by law or expressly
          stated in your agreement. Overdue balances may result in
          suspension after 14 days' written notice.
        </p>
      ),
    },
    {
      id: slug("Customer data"),
      title: "Customer data",
      body: (
        <p>
          Data entered by operators about end-customers ("Customer Data")
          remains the property of the venue. Book &amp; Go processes
          Customer Data as a processor on behalf of the venue, in accordance
          with our Data Processing Addendum. The venue is responsible for
          obtaining all necessary consents from its customers.
        </p>
      ),
    },
    {
      id: slug("Intellectual property"),
      title: "Intellectual property",
      body: (
        <p>
          Book &amp; Go and its licensors retain all rights, title, and
          interest in the Service, including software, trademarks, and
          documentation. We grant you a limited, revocable,
          non-transferable licence to use the Service in accordance with
          these Terms.
        </p>
      ),
    },
    {
      id: slug("Third-party services"),
      title: "Third-party services",
      body: (
        <p>
          The Service may integrate with third-party services (payment
          gateways, messaging providers, mapping). Your use of those
          services is subject to their own terms. Book &amp; Go is not
          responsible for third-party services beyond integrating them.
        </p>
      ),
    },
    {
      id: slug("Service availability"),
      title: "Service availability",
      body: (
        <p>
          We target 99.5% monthly availability, excluding scheduled
          maintenance and events beyond our reasonable control. Service
          credits, if any, are set out in your commercial agreement.
        </p>
      ),
    },
    {
      id: slug("Disclaimers"),
      title: "Disclaimers",
      body: (
        <p>
          To the maximum extent permitted by law, the Service is provided
          "as is" and "as available", without warranties of any kind, whether
          express or implied, including merchantability, fitness for a
          particular purpose, and non-infringement.
        </p>
      ),
    },
    {
      id: slug("Limitation of liability"),
      title: "Limitation of liability",
      body: (
        <p>
          To the maximum extent permitted by law, our aggregate liability
          arising out of or related to the Service shall not exceed the
          fees paid by the venue in the twelve (12) months preceding the
          event giving rise to the claim. We are not liable for indirect,
          incidental, consequential, or punitive damages, or for lost
          profits, revenue, or data.
        </p>
      ),
    },
    {
      id: slug("Termination"),
      title: "Termination",
      body: (
        <p>
          You or the venue may close an account at any time by contacting
          support. We may suspend or terminate access for material breach
          of these Terms or for non-payment, with reasonable notice where
          practicable. On termination, we will retain data per the Privacy
          Policy and applicable law.
        </p>
      ),
    },
    {
      id: slug("Changes to these Terms"),
      title: "Changes to these Terms",
      body: (
        <p>
          We may amend these Terms by posting a revised version with a new
          "Last updated" date. Material changes will be notified at least
          30 days in advance via the Operator Portal or by email. Continued
          use of the Service after the effective date constitutes
          acceptance.
        </p>
      ),
    },
    {
      id: slug("Governing law"),
      title: "Governing law",
      body: (
        <p>
          These Terms are governed by the laws of the jurisdiction set out
          in your venue's commercial agreement with Book &amp; Go.
          Disputes will be resolved in the courts of that jurisdiction,
          unless mandatory consumer protection laws provide otherwise.
        </p>
      ),
    },
    {
      id: slug("Contact"),
      title: "Contact",
      body: (
        <p>
          Questions about these Terms? Contact us at{" "}
          <a href="mailto:legal@bookandgo.app">legal@bookandgo.app</a> or{" "}
          <a href="mailto:support@bookandgo.app">support@bookandgo.app</a>.
        </p>
      ),
    },
  ],
};

export function PrivacyPage() {
  return (
    <LegalPage
      content={PRIVACY}
      otherLink={{ href: "/terms", label: "Terms & Conditions" }}
    />
  );
}

export function TermsPage() {
  return (
    <LegalPage
      content={TERMS}
      otherLink={{ href: "/privacy", label: "Privacy Policy" }}
    />
  );
}

export default PrivacyPage;
